/**
 * Shared bot / crawler / automation user-agent detection.
 *
 * Single source of truth for "is this UA a non-human client?". Used by the
 * Edge middleware and API traffic logs (to tag bot traffic) and by the
 * checkout flow (to skip Stripe.js + suppress checkout-failure alerts for
 * crawlers).
 *
 * Pure leaf module — no imports, no Node/DOM APIs — so it is safe to import
 * from the Edge runtime, API routes, and client components alike.
 */

// Search-engine crawlers, SEO/monitoring bots, headless browsers and HTTP
// automation clients. `bot` / `crawl` / `spider` catch the long tail; the
// named tokens catch crawlers whose UA contains none of those substrings
// (ahrefs, semrush, yandex, baidu, slurp, lighthouse, pingdom).
const BOT_UA_REGEX =
  /bot|crawl|spider|scrape|headless|phantom|puppeteer|playwright|selenium|wget|curl|python-requests|go-http|java\/|httpclient|ahrefs|semrush|yandex|baidu|facebookexternalhit|slurp|lighthouse|pingdom/i;

/**
 * Returns true when the user-agent looks like a bot, crawler, or automation
 * client. A missing user-agent is treated as a bot — real browsers always
 * send one. Callers that only want to act on a *known* UA should guard the
 * call (e.g. `ua && isLikelyBot(ua)`).
 */
export function isLikelyBot(ua: string | null | undefined): boolean {
  if (!ua) return true;
  return BOT_UA_REGEX.test(ua);
}
